import { useEffect, useRef, useState } from "react";
import { Globe, GlobeMark } from "./Globe.ts";

// THE PLANET (Joshua 2026-09-07): the beat opens on Sphere World from
// space - a paper globe spins, the camera drops onto the Great Slab, and
// the globe dissolves into the flat paper map where the trail draws.
// First quest ever: the full spin; later quests: a quick one.
type PlanetData = { w: number; h: number; slab: { lon: number; lat: number }; map_rect: { x0: number; y0: number; x1: number; y1: number } | null };

// THE OVERLAND MAP BEAT (Joshua 2026-09-05/06): quest mode opens on the
// one giant map of Sphere World. A dotted ink trail draws today's road
// across the regions the quest visits, laid over the faded trails of every
// quest before it (localStorage) - regulars watch their coverage grow, and
// the far country stays faint so nobody feels they'll ever see it all.
// Unity fires window "questMap" {quest, regions[]} the moment the quest
// JSON lands; the beat lasts ~2.6s, any tap skips it.

type Region = { name: string; at: [number, number]; spots?: [number, number][] };
type MapData = { w: number; h: number; regions: Record<string, Region> };
type Route = { quest: string; regions: string[]; date: string };

const TRAIL_KEY = "aots.mapTrail";
const BEAT = 4.4;   // the trail draws at a walking pace (Joshua 2026-09-08: still "a little too fast")
const HOP_BEAT = 3.0;   // a mid-quest hop: one new leg, no globe

function loadTrail(): Route[] {
  try {
    const raw = localStorage.getItem(TRAIL_KEY);
    const t = raw ? JSON.parse(raw) : [];
    return Array.isArray(t) ? t : [];
  } catch { return []; }
}
function saveTrail(t: Route[]) {
  try { localStorage.setItem(TRAIL_KEY, JSON.stringify(t.slice(-60))); } catch { /* private mode */ }
}

const QuestMap: React.FC = () => {
  // hop: a mid-quest beat - no globe, short, the road grows by one leg
  const [show, setShow] = useState<{ route: Route; past: Route[]; hop: boolean } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const mapRef = useRef<MapData | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const doneRef = useRef<() => void>(() => {});

  // Prefetch the paper (450KB) once the page is idle - the beat must not
  // wait on the network when the quest starts
  const loadingRef = useRef(false);
  const planetRef = useRef<HTMLImageElement | null>(null);
  const planetJRef = useRef<PlanetData | null>(null);
  const planetFarRef = useRef<HTMLImageElement | null>(null);
  const globeCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const loadPaper = () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    fetch("levels/map.json").then((r) => r.json()).then((d) => { mapRef.current = d; }).catch(() => {});
    const img = new Image();
    img.onload = () => { imgRef.current = img; };
    img.src = "levels/map.jpg";
    fetch("levels/planet.json").then((r) => r.json()).then((d) => { planetJRef.current = d; }).catch(() => {});
    const pl = new Image();
    pl.onload = () => { planetRef.current = pl; };
    pl.src = "levels/planet.jpg";
    const pf = new Image();
    pf.onload = () => { planetFarRef.current = pf; };
    pf.src = "levels/planet-far.jpg";
  };
  useEffect(() => {
    const t = window.setTimeout(loadPaper, 2500);
    return () => window.clearTimeout(t);
  }, []);

  // THE CHOICE MAP (Joshua 2026-09-06: "the selection should also show the
  // map and which direction each option pulls you in"): while a choice card
  // is up, a small map in the free top-left shows where you stand and a
  // dotted road to each option's region, badged A / B in the card's colours.
  const [choice, setChoice] = useState<{ from: string; options: { label: string; region: string }[]; intro?: string; quest?: string; full: boolean; top?: number } | null>(null);
  const choiceRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const onMap = (e: Event) => {
      const d = (e as CustomEvent).detail as { quest?: string; regions?: string[]; hop?: boolean; choice?: boolean; roads?: boolean; top?: number; intro?: string; from?: string; options?: { label: string; region: string }[] };
      if (d?.choice || d?.roads) {
        loadPaper();
        // a CHOICE node is a full map screen with A/B buttons (Joshua
        // 2026-09-07: "see clearly how each choice implies a different
        // route"); a ROAD pick keeps the Unity card and gets a mini map in
        // the gap above it
        setChoice({ from: d.from || "village", options: (d.options || []).slice(0, 3), intro: d.intro, quest: d.quest, full: !!d.choice, top: d.top });
        return;
      }
      const regions = (d?.regions || []).filter((r) => typeof r === "string");
      if (!regions.length) return;
      loadPaper();   // a quest started before the idle prefetch: fetch now
      const past = loadTrail();
      const route: Route = { quest: d.quest || "Quest", regions, date: new Date().toISOString().slice(0, 10) };
      // replaying today's quest re-draws rather than doubling the trail
      const prior = past.length && past[past.length - 1].quest === route.quest ? past.slice(0, -1) : past;
      saveTrail([...prior, route]);
      setShow({ route, past: prior, hop: !!d.hop });
    };
    const onHide = () => setChoice(null);
    window.addEventListener("questMap", onMap);
    window.addEventListener("questMapHide", onHide);
    return () => { window.removeEventListener("questMap", onMap); window.removeEventListener("questMapHide", onHide); };
  }, []);

  useEffect(() => {
    if (!choice) return;
    let tries = 0;
    let timer = 0;
    const draw = () => {
      const cv = choiceRef.current, map = mapRef.current, img = imgRef.current;
      if (!cv) return;
      if (!map || !img) { if (tries++ < 20) timer = window.setTimeout(draw, 250); return; }
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const portrait = window.innerHeight > window.innerWidth;
      // full: the map fills the screen above the two buttons; mini: the free
      // corner above the card (its top comes from Unity), never under it
      const gap = choice.top ? Math.max(120, choice.top * window.innerHeight - 74) : window.innerHeight * 0.30;
      // the mini map stops short of the webcam panel (~190px wide, top-right)
      const W = choice.full ? window.innerWidth : portrait ? Math.min(window.innerWidth * 0.62, window.innerWidth - 200) : 360;
      const H = choice.full ? window.innerHeight - 170 : portrait ? Math.min(gap, window.innerHeight * 0.30) : 220;
      cv.width = W * dpr; cv.height = H * dpr;
      cv.style.width = `${W}px`; cv.style.height = `${H}px`;
      const g = cv.getContext("2d");
      if (!g) return;
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      const pt = (key: string): [number, number] | null => {
        const r = map.regions[key];
        return r ? [r.at[0] * map.w, r.at[1] * map.h] : null;
      };
      const from = pt(choice.from) || pt("village")!;
      const tos = choice.options.map((o) => pt(o.region));
      const all = [from, ...tos.filter(Boolean) as [number, number][]];
      let x0 = Math.min(...all.map((p) => p[0])), x1 = Math.max(...all.map((p) => p[0]));
      let y0 = Math.min(...all.map((p) => p[1])), y1 = Math.max(...all.map((p) => p[1]));
      const minSpan = map.w * 0.22;
      if (x1 - x0 < minSpan) { const c = (x0 + x1) / 2; x0 = c - minSpan / 2; x1 = c + minSpan / 2; }
      if (y1 - y0 < minSpan * 0.6) { const c = (y0 + y1) / 2; y0 = c - minSpan * 0.3; y1 = c + minSpan * 0.3; }
      const scale = Math.min(W / ((x1 - x0) * (choice.full ? 1.35 : 1.5)), H / ((y1 - y0) * (choice.full ? 1.6 : 1.5)), choice.full ? 1.4 : 1);
      const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
      // full screen: the webcam owns the top-right, so the map's content sits
      // left of centre and a little low (flow gate 2026-09-07)
      const ox = choice.full && portrait ? W * 0.40 : W / 2, oy = choice.full && portrait ? H * 0.56 : H / 2;
      const S = (p: [number, number]): [number, number] => [ox + (p[0] - cx) * scale, oy + (p[1] - cy) * scale];
      // paper in a rounded frame
      g.clearRect(0, 0, W, H);
      g.save();
      const rr = (x: number, y: number, w: number, h: number, r: number) => {
        g.beginPath(); g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r);
        g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath();
      };
      rr(0, 0, W, H, 10); g.clip();
      g.fillStyle = "#1a1410"; g.fillRect(0, 0, W, H);
      g.drawImage(img, ox - cx * scale, oy - cy * scale, map.w * scale, map.h * scale);
      g.restore();
      g.strokeStyle = "rgba(245,230,200,0.7)"; g.lineWidth = 1.5;
      rr(0.75, 0.75, W - 1.5, H - 1.5, 10); g.stroke();
      // roads out, one per option
      const cols = ["rgba(204,107,31,1)", "rgba(77,107,184,1)", "rgba(60,140,90,1)"];
      g.lineCap = "round";
      const fs = S(from);
      choice.options.forEach((o, i) => {
        const t = tos[i]; if (!t) return;
        const ts = S(t);
        g.strokeStyle = cols[i]; g.lineWidth = 3.5; g.setLineDash([0.01, 8]);
        g.beginPath(); g.moveTo(fs[0], fs[1]); g.lineTo(ts[0], ts[1]); g.stroke();
        // the pulse: a bright bead runs from you toward each destination
        const w = ((performance.now() / 1400 + i * 0.5) % 1);
        const bx0 = fs[0] + (ts[0] - fs[0]) * w, by0 = fs[1] + (ts[1] - fs[1]) * w;
        g.setLineDash([]);
        g.save(); g.shadowColor = cols[i]; g.shadowBlur = 12; g.fillStyle = "rgba(255,240,200,1)";
        g.beginPath(); g.arc(bx0, by0, 5, 0, Math.PI * 2); g.fill(); g.restore();
        // A / B badge at the destination
        g.fillStyle = cols[i];
        g.beginPath(); g.arc(ts[0], ts[1], 11, 0, Math.PI * 2); g.fill();
        g.fillStyle = "#fff"; g.font = "700 13px Georgia, serif"; g.textAlign = "center"; g.textBaseline = "middle";
        g.fillText(String.fromCharCode(65 + i), ts[0], ts[1] + 0.5);
        // no name under the badge: the paper already prints every place name
        // and the buttons say "to <region>" (flow gate: doubled labels)
        g.textBaseline = "alphabetic";
      });
      // you are here
      g.fillStyle = "rgba(140,30,20,1)";
      g.beginPath(); g.arc(fs[0], fs[1], 5.5, 0, Math.PI * 2); g.fill();
      g.strokeStyle = "rgba(140,30,20,0.6)"; g.lineWidth = 2;
      g.beginPath(); g.arc(fs[0], fs[1], 10, 0, Math.PI * 2); g.stroke();
      g.font = "700 10px Georgia, serif"; g.textAlign = "left"; g.fillStyle = "rgba(245,230,200,0.9)";
      if (!choice.full) {
        // heading on its own band; the map's edges fade so cropped place
        // names read as the edge of the paper, not clipped text (polish gate)
        g.save(); rr(0, 0, W, H, 10); g.clip();
        const fadeL = g.createLinearGradient(0, 0, 26, 0); fadeL.addColorStop(0, "rgba(26,20,16,0.85)"); fadeL.addColorStop(1, "rgba(26,20,16,0)");
        const fadeR = g.createLinearGradient(W - 26, 0, W, 0); fadeR.addColorStop(0, "rgba(26,20,16,0)"); fadeR.addColorStop(1, "rgba(26,20,16,0.85)");
        const fadeB = g.createLinearGradient(0, H - 22, 0, H); fadeB.addColorStop(0, "rgba(26,20,16,0)"); fadeB.addColorStop(1, "rgba(26,20,16,0.85)");
        g.fillStyle = fadeL; g.fillRect(0, 0, 26, H); g.fillStyle = fadeR; g.fillRect(W - 26, 0, 26, H); g.fillStyle = fadeB; g.fillRect(0, H - 22, W, 22);
        g.restore();
        // a small tab, not a bar: the paper stays readable under it
        g.fillStyle = "rgba(26,20,16,0.85)"; g.fillRect(0, 0, 96, 22);
        g.font = "700 11px Georgia, serif"; g.textAlign = "left"; g.fillStyle = "rgba(245,230,200,0.95)";
        g.fillText("WHICH ROAD?", 8, 15);
      }
      else {
        // the story line and the question, on a dark band so the map's own
        // names don't fight them
        // the story band sits at the BOTTOM of the map, clear of the webcam
        // panel and its pills (flow gate 2026-09-07)
        g.textAlign = "center";
        g.font = `500 ${Math.min(16, W * 0.04)}px Georgia, serif`;
        const words = (choice.intro || "Which road?").split(" "); const lines: string[] = []; let cur = "";
        for (const wd of words) { const t = cur ? cur + " " + wd : wd; if (g.measureText(t).width > W * 0.86) { lines.push(cur); cur = wd; } else cur = t; }
        if (cur) lines.push(cur);
        const bandH = 58 + lines.length * 20;
        g.fillStyle = "rgba(20,14,10,0.72)"; g.fillRect(0, H - bandH, W, bandH);
        const cap = (choice.quest || "").toUpperCase();
        g.font = `700 ${Math.min(22, W * 0.05)}px Georgia, serif`; g.lineWidth = 4; g.strokeStyle = "rgba(20,14,10,0.9)"; g.fillStyle = "rgba(245,230,200,1)";
        g.strokeText(cap, W / 2, H - bandH + 26); g.fillText(cap, W / 2, H - bandH + 26);
        g.font = `500 ${Math.min(16, W * 0.04)}px Georgia, serif`;
        lines.forEach((ln, i) => { g.strokeText(ln, W / 2, H - bandH + 50 + i * 20); g.fillText(ln, W / 2, H - bandH + 50 + i * 20); });
        g.font = "700 12px Georgia, serif"; g.fillStyle = "rgba(245,230,200,0.85)";
        g.strokeText("WHICH ROAD?", W / 2, H - 8); g.fillText("WHICH ROAD?", W / 2, H - 8);
      }
    };
    draw();
    const pulse = window.setInterval(draw, 90);   // the roads pulse (Joshua 2026-09-07)
    window.addEventListener("resize", draw);
    return () => { window.clearTimeout(timer); window.clearInterval(pulse); window.removeEventListener("resize", draw); };
  }, [choice]);

  useEffect(() => {
    if (!show) return;
    const cv = canvasRef.current;
    if (!cv) return;
    let raf = 0;
    let killed = false;
    const tEvent = performance.now();
    let t0 = -1;   // the clock starts once the paper is in hand
    let skipAt = -1;

    let globe: Globe | null = null;
    let globeDur = -1;   // decided once the assets are in hand
    const finish = () => { if (!killed) { killed = true; cancelAnimationFrame(raf); if (globe) globe.dispose(); setShow(null); } };
    doneRef.current = () => { if (skipAt < 0) skipAt = performance.now(); };
    const ease = (x: number) => 1 - Math.pow(1 - Math.min(1, Math.max(0, x)), 3);
    const easeIO = (x: number) => { x = Math.min(1, Math.max(0, x)); return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2; };

    const draw = () => {
      if (killed) return;
      const map = mapRef.current, img = imgRef.current;
      const now = performance.now();
      // nothing to draw yet (first-ever quest before the prefetch landed):
      // give the paper a moment, then let the quest go on without the beat
      if (!map || !img) {
        if (now - tEvent > 2500 || skipAt >= 0) { finish(); return; }
        raf = requestAnimationFrame(draw); return;
      }
      if (t0 < 0) {
        t0 = now;
        // the globe only plays when the planet arrived with the paper
        const pj = planetJRef.current, pi = planetRef.current, gc = globeCanvasRef.current;
        if (pj && pi && gc && !show.hop) {
          globe = new Globe(gc);
          globe.setTexture(pi, planetFarRef.current);
          globeDur = globe.ready ? (show.past.length ? 4.2 : 6.8) : 0;   // slower again (Joshua 2026-09-08)
        } else globeDur = 0;
      }
      let t = (now - t0) / 1000 - globeDur;
      const beat = show.hop ? HOP_BEAT : BEAT;
      // a skip jumps straight into the fade-out
      if (skipAt >= 0) t = Math.max(t, beat - 0.3 + (now - skipAt) / 1000);
      if (t >= beat) { finish(); return; }

      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const W = window.innerWidth, H = window.innerHeight;

      // ---- the globe: spin the Slab into view, then drop onto it ----
      const gc = globeCanvasRef.current;
      if (globe && gc && t < 0.45) {
        const pj = planetJRef.current!;
        const tg = t + globeDur;                 // 0..globeDur
        const k = globeDur > 0 ? tg / globeDur : 1;
        const slabLon = pj.slab.lon * Math.PI / 180, slabLat = -pj.slab.lat * Math.PI / 180;
        const spin = easeIO(Math.min(1, k / 0.62));           // most of the spin first
        const zoom = ease(Math.max(0, (k - 0.42) / 0.58));    // then the descent
        const lon = slabLon + (1 - spin) * (show.past.length ? 2.2 : 4.4);
        const lat = slabLat * spin + (1 - spin) * 0.35;
        const dist = 3.1 - (3.1 - 1.32) * zoom;   // from well out (a big world) down until the small Slab fills the frame
        // dip to dark at the hand-off instead of a double exposure (flow gate)
        const fadeIn = Math.min(1, tg / 0.35) * (t > -0.3 ? Math.max(0, -t / 0.3) : 1);
        const fadeOut = t > 0 ? 0 : 1;
        // ink marks: every region visited so far (and today's road)
        const marks: GlobeMark[] = [];
        if (pj.map_rect) {
          const seen = new Set<string>();
          for (const r of [...show.past, show.route]) for (const key of r.regions) {
            const rg = map.regions[key]; if (!rg || seen.has(key)) continue; seen.add(key);
            marks.push([pj.map_rect.x0 + rg.at[0] * (pj.map_rect.x1 - pj.map_rect.x0), pj.map_rect.y0 + rg.at[1] * (pj.map_rect.y1 - pj.map_rect.y0)]);
          }
        }
        globe.draw(Math.round(W * dpr), Math.round(H * dpr), lon, lat, dist, fadeIn, marks, ease(Math.max(0, (zoom - 0.25) / 0.6)));
        gc.style.opacity = String(fadeOut);
        if (t < 0) { raf = requestAnimationFrame(draw); return; }   // paper not yet
      } else if (gc) gc.style.opacity = "0";

      if (cv.width !== W * dpr || cv.height !== H * dpr) { cv.width = W * dpr; cv.height = H * dpr; }
      const g = cv.getContext("2d");
      if (!g) return;
      g.setTransform(dpr, 0, 0, dpr, 0, 0);

      const fade = t < 0.35 ? t / 0.35 : t > beat - 0.3 ? (beat - t) / 0.3 : 1;
      g.globalAlpha = 1;
      g.fillStyle = "#1a1410";
      g.fillRect(0, 0, W, H);

      // --- the camera: frame the road travelled so far (Joshua 2026-09-12:
      // no old trail, no route ahead - nobody remembers the last quest and
      // the road ahead was a spoiler)
      const pt = (key: string): [number, number] | null => {
        const r = map.regions[key];
        return r ? [r.at[0] * map.w, r.at[1] * map.h] : null;
      };
      const todayPts = show.route.regions.map(pt).filter(Boolean) as [number, number][];
      if (!todayPts.length) { finish(); return; }
      const framePts = todayPts;
      let x0 = Math.min(...framePts.map((p) => p[0])), x1 = Math.max(...framePts.map((p) => p[0]));
      let y0 = Math.min(...framePts.map((p) => p[1])), y1 = Math.max(...framePts.map((p) => p[1]));
      const minSpan = map.w * 0.26;
      if (x1 - x0 < minSpan) { const c = (x0 + x1) / 2; x0 = c - minSpan / 2; x1 = c + minSpan / 2; }
      if (y1 - y0 < minSpan * 0.66) { const c = (y0 + y1) / 2; y0 = c - minSpan * 0.33; y1 = c + minSpan * 0.33; }
      const pad = 0.42;
      const bw = (x1 - x0) * (1 + pad), bh = (y1 - y0) * (1 + pad);
      let scale = Math.min(W / bw, H / bh);
      scale = Math.min(scale, 1.1);   // never blow the paper up past its grain
      // open WIDE on the whole land, then ease in toward today's road while
      // it draws (Joshua 2026-09-07: "the map should seem bigger")
      scale *= show.hop ? 0.9 + 0.1 * ease(t / 1.2) : 0.62 + 0.38 * (1 - Math.pow(1 - Math.min(1, t / (BEAT * 0.8)), 3));
      const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
      const toScreen = (p: [number, number]): [number, number] =>
        [W / 2 + (p[0] - cx) * scale, H / 2 + (p[1] - cy) * scale];

      g.globalAlpha = fade;
      g.drawImage(img, W / 2 - cx * scale, H / 2 - cy * scale, map.w * scale, map.h * scale);
      // vignette: the far country stays half-seen
      const vg = g.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.35, W / 2, H / 2, Math.max(W, H) * 0.75);
      vg.addColorStop(0, "rgba(26,20,16,0)");
      vg.addColorStop(1, "rgba(26,20,16,0.85)");
      g.fillStyle = vg; g.fillRect(0, 0, W, H);

      // --- old trails: faded ink, all at once
      g.lineCap = "round"; g.lineJoin = "round";
      const dots = (pts: [number, number][], upto: number, ink: string, width: number) => {
        if (pts.length < 2 && upto >= 1) { return; }
        g.strokeStyle = ink; g.lineWidth = width;
        g.setLineDash([0.01, width * 2.6]);
        g.beginPath();
        // walk the polyline to the fraction `upto`
        let total = 0;
        for (let i = 1; i < pts.length; i++) total += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
        let left = total * upto;
        g.moveTo(pts[0][0], pts[0][1]);
        for (let i = 1; i < pts.length && left > 0; i++) {
          const seg = Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
          if (seg <= left) { g.lineTo(pts[i][0], pts[i][1]); left -= seg; }
          else {
            const f = left / seg;
            g.lineTo(pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * f, pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * f);
            left = 0;
          }
        }
        g.stroke();
        g.setLineDash([]);
      };
      // WHERE I'VE BEEN (Joshua 2026-09-07: "too busy with dotted lines"):
      // no old roads - each visited region wears a small ink flag with its
      // visit count
      const visits = new Map<string, number>();
      for (const r of show.past) for (const k of new Set(r.regions)) visits.set(k, (visits.get(k) || 0) + 1);
      visits.forEach((n, key) => {
        const p = pt(key); if (!p) return;
        const [sx, sy] = toScreen(p);
        g.globalAlpha = fade;
        g.strokeStyle = "rgba(60,40,22,0.9)"; g.lineWidth = 2;
        g.beginPath(); g.moveTo(sx + 9, sy - 2); g.lineTo(sx + 9, sy - 26); g.stroke();
        g.fillStyle = "rgba(140,30,20,0.95)";
        g.beginPath(); g.moveTo(sx + 9, sy - 26); g.lineTo(sx + 24, sy - 21); g.lineTo(sx + 9, sy - 15); g.closePath(); g.fill();
        if (n > 1) { g.fillStyle = "rgba(40,22,10,1)"; g.font = "700 10px Georgia, serif"; g.textAlign = "left"; g.fillText(String(n), sx + 26, sy - 17); }
      });

      // --- today's road: the legs already walked are inked at once; the
      // newest leg draws itself, and the pulse rides it
      const roadPts = todayPts.map(toScreen);
      const prog = Math.min(1, Math.max(0, (t - 0.4) / (show.hop ? 1.6 : 2.9)));
      const legEased = 1 - Math.pow(1 - prog, 2.2);
      const legLen = roadPts.map((p, i) => i ? Math.hypot(p[0] - roadPts[i - 1][0], p[1] - roadPts[i - 1][1]) : 0);
      const roadTotal = legLen.reduce((a, b) => a + b, 0);
      const walked = roadTotal > 0 ? (roadTotal - legLen[legLen.length - 1]) / roadTotal : 0;   // fraction inked before this beat
      const eased = walked + (1 - walked) * legEased;
      if (roadPts.length >= 2) {
        dots(roadPts, eased, "rgba(58,34,14,0.95)", 4.2);
        // the pulse: a bright wave travelling along the drawn road (Joshua
        // 2026-09-07: "highlight the path or blink it")
        const wave = ((t * 0.55) % 1);
        g.save();
        g.globalAlpha = fade * 0.85;
        g.setLineDash([0.01, 4.2 * 2.6]); g.lineWidth = 6; g.strokeStyle = "rgba(255,196,90,1)";
        g.shadowColor = "rgba(255,196,90,0.9)"; g.shadowBlur = 10;
        // draw only a short window of the road around the wave front
        const total = roadPts.reduce((a, p, i) => i ? a + Math.hypot(p[0] - roadPts[i - 1][0], p[1] - roadPts[i - 1][1]) : 0, 0);
        // the wave runs along the newest leg only
        const from = walked + Math.max(0, wave - 0.16) * (eased - walked), to = walked + Math.min(1, wave) * (eased - walked);
        let acc = 0;
        g.beginPath();
        for (let i = 1; i < roadPts.length; i++) {
          const a = roadPts[i - 1], b = roadPts[i];
          const seg = Math.hypot(b[0] - a[0], b[1] - a[1]);
          const s0 = acc / total, s1 = (acc + seg) / total; acc += seg;
          const lo = Math.max(s0, from), hi = Math.min(s1, to);
          if (hi <= lo) continue;
          const f0 = (lo - s0) / (s1 - s0), f1 = (hi - s0) / (s1 - s0);
          g.moveTo(a[0] + (b[0] - a[0]) * f0, a[1] + (b[1] - a[1]) * f0);
          g.lineTo(a[0] + (b[0] - a[0]) * f1, a[1] + (b[1] - a[1]) * f1);
        }
        g.stroke();
        g.restore();
      }

      // region pins, planted as the pen reaches them (the paper already
      // prints every name - a second label on top read as a smudge)
      const pinAt = (p: [number, number], age: number) => {
        const [sx, sy] = toScreen(p);
        const pulse = 1 + 0.5 * Math.max(0, 1 - age * 2.5);
        g.globalAlpha = fade;
        g.strokeStyle = "rgba(140,30,20,0.55)"; g.lineWidth = 2;
        g.beginPath(); g.arc(sx, sy, 11 * pulse, 0, Math.PI * 2); g.stroke();
        g.fillStyle = "rgba(140,30,20,1)";
        g.beginPath(); g.arc(sx, sy, 5.5, 0, Math.PI * 2); g.fill();
      };
      const todayKeys = show.route.regions;
      const n = todayKeys.length;
      for (let i = 0; i < n; i++) {
        const r = map.regions[todayKeys[i]];
        if (!r) continue;
        // pins already reached sit still; the newest one lands as the pen
        // arrives (the opening beat's lone pin lands with the fade-in)
        if (i < n - 1) pinAt([r.at[0] * map.w, r.at[1] * map.h], 9);
        else if (n === 1 || legEased >= 0.999) pinAt([r.at[0] * map.w, r.at[1] * map.h], n === 1 ? t - 0.35 : t - 0.4 - (show.hop ? 1.6 : 2.9));
      }

      // caption
      g.globalAlpha = fade;
      g.textAlign = "center";
      g.font = `700 ${Math.max(13, Math.min(20, W * 0.045))}px Georgia, "Times New Roman", serif`;
      g.lineWidth = 4; g.strokeStyle = "rgba(20,14,10,0.9)";
      g.fillStyle = "rgba(245,230,200,1)";
      const cap = show.hop
        ? "ONWARD TO " + (map.regions[todayKeys[n - 1]]?.name || todayKeys[n - 1]).toUpperCase()
        : show.route.quest.toUpperCase();
      g.strokeText(cap, W / 2, H - 58); g.fillText(cap, W / 2, H - 58);
      g.font = `500 ${Math.max(11, Math.min(14, W * 0.032))}px Georgia, "Times New Roman", serif`;
      g.fillStyle = "rgba(245,230,200,0.75)";
      g.strokeText("SPHERE WORLD  ·  tap to ride on", W / 2, H - 34);
      g.fillText("SPHERE WORLD  ·  tap to ride on", W / 2, H - 34);
      g.globalAlpha = 1;

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { killed = true; cancelAnimationFrame(raf); };
  }, [show]);

  const regionName = (key: string) => mapRef.current?.regions[key]?.name || key;
  const pick = (i: number) => {
    try { (window as unknown as { unityInstance?: { SendMessage: (o: string, m: string, a?: number) => void } }).unityInstance?.SendMessage("Manager", "QuestChoice", i); } catch { /* not loaded */ }
    setChoice(null);
  };
  return (
    <>
      {choice && !show && !choice.full && (
        <canvas
          ref={choiceRef}
          style={{ position: "fixed", left: 10, top: "calc(60px + env(safe-area-inset-top, 0px))", zIndex: 20,
                   pointerEvents: "none", borderRadius: 10, boxShadow: "0 4px 18px rgba(0,0,0,0.45)" }}
        />
      )}
      {choice && !show && choice.full && (
        <div style={{ position: "fixed", inset: 0, zIndex: 20, background: "#1a1410", display: "flex", flexDirection: "column" }}>
          <canvas ref={choiceRef} style={{ display: "block", flex: "0 0 auto" }} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10, padding: "12px 16px calc(14px + env(safe-area-inset-bottom, 0px))", justifyContent: "center" }}>
            {choice.options.map((o, i) => (
              <button key={i} onClick={() => pick(i)} style={{
                display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "14px 16px", borderRadius: 10, border: "2px solid rgba(255,255,255,0.25)",
                background: ["#cc6b1f", "#4d6bb8", "#3c8c5a"][i], color: "#fff", fontSize: 17, fontWeight: 700, textAlign: "left", cursor: "pointer",
              }}>
                <span style={{ width: 34, height: 34, borderRadius: 17, background: "rgba(255,255,255,0.22)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "Georgia, serif", flex: "0 0 auto" }}>{String.fromCharCode(65 + i)}</span>
                <span style={{ flex: 1 }}>{o.label}<div style={{ fontSize: 12, fontWeight: 500, opacity: 0.85 }}>to {regionName(o.region)}</div></span>
              </button>
            ))}
          </div>
        </div>
      )}
      {show && (
        <>
          <canvas
            ref={canvasRef}
            onPointerDown={() => doneRef.current()}
            style={{ position: "fixed", inset: 0, width: "100vw", height: "100vh", zIndex: 20, touchAction: "none" }}
          />
          <canvas
            ref={globeCanvasRef}
            onPointerDown={() => doneRef.current()}
            style={{ position: "fixed", inset: 0, width: "100vw", height: "100vh", zIndex: 21, touchAction: "none", opacity: 0 }}
          />
        </>
      )}
    </>
  );
};

export default QuestMap;
